import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import type { RootStore } from '.';
import type { Card } from './card';
import { trackAction } from './analytics';
import type { TrackLabel } from './analytics';
import { getReadableConnectionRowLabel } from './breakoutReadableRowLabel';
import { getStaticStepDisplay } from './stepDisplayUtils';
import { STEP_TAB_DONE, STEP_TAB_TRIAGE } from './ui-active-session';

export type BreakoutPosition = 'after-source' | 'top' | 'bottom';
export type BreakoutWizardStep = 'intro' | 'module' | 'step' | 'position' | 'original' | 'review';

export interface BreakoutModuleOption {
  id: string;
  name: string;
  color: string;
  totalConnections: number;
  disabled: boolean;
}

export interface BreakoutStepOption {
  id: string | null;
  name: string;
  isCurrent: boolean;
}

export class UiBreakoutStore {
  private rootStore: RootStore;

  isDialogOpen = false;
  sourceCardIds: string[] = [];
  triggeredModuleId: string | null = null;
  selectedModuleId: string | null = null;
  currentWizardStep: BreakoutWizardStep = 'module';
  keepOriginals = true;
  targetStepId: string | null = null;
  position: BreakoutPosition = 'after-source';
  showPreview = false;

  hasSeenIntro = false;
  isExecuting = false;
  createdCount = 0;
  cancelRequested = false;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;

    makeObservable(this, {
      isDialogOpen: observable,
      sourceCardIds: observable.ref,
      triggeredModuleId: observable,
      selectedModuleId: observable,
      currentWizardStep: observable,
      keepOriginals: observable,
      targetStepId: observable,
      position: observable,
      showPreview: observable,
      hasSeenIntro: observable,
      isExecuting: observable,
      createdCount: observable,
      cancelRequested: observable,
      sourceCards: computed,
      moduleOptions: computed,
      totalNewCards: computed,
      stepOptions: computed,
      canUseAfterSource: computed,
      isSingleCardFixedModuleMode: computed,
      shouldStartWithModuleSelection: computed,
      canMoveToNextWizardStep: computed,
      reviewSummary: computed,
      reviewItems: computed,
      reviewPlacementSentence: computed,
      reviewOriginalsSentence: computed,
      reviewCreationSentence: computed,
      reviewModuleSentence: computed,
      reviewSummaryItems: computed,
      sourceContext: computed,
      previewGroups: computed,
      openDialog: action.bound,
      closeDialog: action.bound,
      dismissIntro: action.bound,
      setSelectedModuleId: action.bound,
      setKeepOriginals: action.bound,
      setTargetStepId: action.bound,
      setPosition: action.bound,
      goToWizardStep: action.bound,
      goToNextWizardStep: action.bound,
      goToPreviousWizardStep: action.bound,
      togglePreview: action.bound,
      execute: action.bound,
      cancel: action.bound,
    });
  }

  get sourceCards(): Card[] {
    return this.sourceCardIds
      .map((id) => this.rootStore.cardsStore.cards[id])
      .filter((card): card is Card => !!card);
  }

  get moduleOptions(): BreakoutModuleOption[] {
    const session = this.rootStore.sessionsStore.currentSession;
    if (!session || this.sourceCards.length === 0) return [];

    return session.visibleModules.map((module) => {
      const totalConnections = this.sourceCards.reduce(
        (sum, card) => sum + card.getConnectedRows(module.id).length,
        0,
      );
      return {
        id: module.id,
        name: module.name,
        color: module.color,
        totalConnections,
        disabled: totalConnections === 0,
      };
    });
  }

  get totalNewCards(): number {
    if (!this.selectedModuleId) return 0;
    return this.sourceCards.reduce(
      (sum, card) => sum + card.getConnectedRows(this.selectedModuleId as string).length,
      0,
    );
  }

  get stepOptions(): BreakoutStepOption[] {
    const session = this.rootStore.sessionsStore.currentSession;
    const pendingLabel = getStaticStepDisplay(STEP_TAB_TRIAGE)?.label ?? 'Pending';
    const currentStepId = this.getDefaultTargetStepId();
    const orderedSteps =
      session?.sortedSteps.map((step) => ({
        id: step.id,
        name: step.name,
        isCurrent: step.id === currentStepId,
      })) ?? [];

    return [
      {
        id: null,
        name: pendingLabel,
        isCurrent: currentStepId === null,
      },
      ...orderedSteps,
    ];
  }

  get canUseAfterSource(): boolean {
    if (this.sourceCards.length === 0) return false;
    return this.sourceCards.every((card) => (card.data.stepId ?? null) === this.targetStepId);
  }

  get isSingleCardFixedModuleMode(): boolean {
    return this.sourceCardIds.length === 1 && !!this.triggeredModuleId;
  }

  get shouldStartWithModuleSelection(): boolean {
    return !this.isSingleCardFixedModuleMode && this.currentWizardStep === 'module';
  }

  get canMoveToNextWizardStep(): boolean {
    if (this.currentWizardStep === 'module') return !!this.selectedModuleId;
    return true;
  }

  get reviewSummary(): { primary: string; secondary: string } {
    const sourceCardLabel = `${this.sourceCardIds.length} ${
      this.sourceCardIds.length === 1 ? 'card' : 'cards'
    }`;
    const primary = `${this.totalNewCards} ${
      this.totalNewCards === 1 ? 'variation' : 'variations'
    } will be created from ${sourceCardLabel}`;
    const secondary = this.keepOriginals
      ? `${
          this.sourceCardIds.length === 1
            ? 'Original card'
            : `${this.sourceCardIds.length} original cards`
        } will stay in place`
      : `${
          this.sourceCardIds.length === 1
            ? 'Original card'
            : `${this.sourceCardIds.length} original cards`
        } will be deleted`;

    return { primary, secondary };
  }

  get reviewItems(): Array<{ step: BreakoutWizardStep; label: string; value: string }> {
    const items: Array<{ step: BreakoutWizardStep; label: string; value: string }> = [];

    if (!this.isSingleCardFixedModuleMode && this.selectedModuleId) {
      const selectedModule = this.moduleOptions.find(
        (option) => option.id === this.selectedModuleId,
      );
      items.push({
        step: 'module',
        label: 'Module',
        value: selectedModule?.name ?? 'Unknown module',
      });
    }

    const selectedStep = this.stepOptions.find((option) => option.id === this.targetStepId);
    items.push({
      step: 'step',
      label: 'Step',
      value: selectedStep?.name ?? 'Pending',
    });

    const positionValue =
      this.position === 'after-source'
        ? 'Below Original'
        : this.position === 'top'
        ? 'Top Of Step'
        : 'Bottom Of Step';
    items.push({
      step: 'position',
      label: 'Position',
      value: positionValue,
    });

    items.push({
      step: 'original',
      label: 'Original Cards',
      value: this.keepOriginals ? 'Keep Original Card' : 'Delete Original Card',
    });

    return items;
  }

  get reviewPlacementSentence(): string {
    const stepName =
      this.stepOptions.find((o) => (o.id ?? null) === this.targetStepId)?.name ?? 'Pending';
    if (this.position === 'after-source')
      return 'New variants will be placed directly below each original card.';
    if (this.position === 'top')
      return `New variants will be placed at the top of the ${stepName} step.`;
    return `New variants will be placed at the bottom of the ${stepName} step.`;
  }

  get reviewModuleSentence(): string | null {
    if (!this.selectedModuleId) return null;
    const selectedModule = this.moduleOptions.find((o) => o.id === this.selectedModuleId);
    if (!selectedModule) return null;
    return `Breaking out by ${selectedModule.name}`;
  }

  get reviewCreationSentence(): string | null {
    if (!this.selectedModuleId) return null;
    const selectedModule = this.moduleOptions.find((o) => o.id === this.selectedModuleId);
    if (!selectedModule) return null;

    if (this.sourceCardIds.length === 1) {
      return `${this.totalNewCards} ${
        this.totalNewCards === 1 ? 'Variation' : 'Variations'
      } will be created from the connections in the ${
        selectedModule.name
      } module of your selected cards`;
    }

    return `${this.totalNewCards} ${
      this.totalNewCards === 1 ? 'variation' : 'variations'
    } will be created from the connections in the ${selectedModule.name} module on selected cards.`;
  }

  get reviewSummaryItems(): Array<{
    text: string;
    jumpTo: 'module' | 'step' | 'position' | 'original';
  }> {
    const items: Array<{
      text: string;
      jumpTo: 'module' | 'step' | 'position' | 'original';
    }> = [];

    if (this.reviewCreationSentence) {
      items.push({ text: this.reviewCreationSentence, jumpTo: 'module' });
    }

    items.push({ text: this.reviewPlacementSentence, jumpTo: 'step' });
    items.push({ text: this.reviewOriginalsSentence, jumpTo: 'original' });

    return items;
  }

  get reviewOriginalsSentence(): string {
    const count = this.sourceCardIds.length;
    const cardLabel = count === 1 ? '1 original card' : `${count} original cards`;
    return this.keepOriginals
      ? `${cardLabel} will remain in place after the breakout has been completed.`
      : `${cardLabel} will be removed after the breakout has been completed.`;
  }

  get sourceContext(): {
    cardTitle: string | null;
    cardNumber: number | null;
    selectedCardCount: number;
    moduleName: string | null;
  } | null {
    if (this.sourceCards.length === 0) return null;

    const firstCard = this.sourceCards[0];
    const session = this.rootStore.sessionsStore.currentSession;
    const contextModuleId = this.triggeredModuleId ?? this.selectedModuleId;
    const moduleName = contextModuleId
      ? session?.data?.modules?.[contextModuleId]?.name ??
        session?.visibleModules?.find((module) => module.id === contextModuleId)?.name ??
        null
      : null;

    return {
      cardTitle: this.sourceCards.length === 1 ? firstCard.mainLabel || null : null,
      cardNumber: this.sourceCards.length === 1 ? firstCard.cardNumber : null,
      selectedCardCount: this.sourceCards.length,
      moduleName,
    };
  }

  get previewGroups(): Array<{
    sourceCardId: string;
    sourceCardTitle: string;
    sourceCardNumber: number;
    rows: string[];
  }> {
    if (!this.selectedModuleId) return [];

    const session = this.rootStore.sessionsStore.currentSession;
    const module = session?.data.modules[this.selectedModuleId];
    const source = module ? this.rootStore.sourcesStore.sources[module.sourceId] : undefined;

    return this.sourceCards.map((card) => {
      const rows = card.getConnectedRows(this.selectedModuleId as string).map((rowId) => {
        const row = source?.rows[rowId];
        return getReadableConnectionRowLabel(row, source, { fallback: 'No Connection' });
      });

      return {
        sourceCardId: card.id,
        sourceCardTitle: card.mainLabel || `Card #${card.cardNumber}`,
        sourceCardNumber: card.cardNumber,
        rows,
      };
    });
  }

  private getDefaultTargetStepId(): string | null {
    const currentStepTabId = this.rootStore.uiActiveSessionStore.currentStepTabId;
    if (currentStepTabId === STEP_TAB_TRIAGE) return null;
    if (currentStepTabId === STEP_TAB_DONE) return null;
    return currentStepTabId;
  }

  openDialog(cardIds: string[], preselectedModuleId?: string, entryLabel?: TrackLabel) {
    const uniqueIds = Array.from(new Set(cardIds));
    this.sourceCardIds = uniqueIds;
    this.triggeredModuleId = preselectedModuleId ?? null;

    this.keepOriginals = true;
    this.showPreview = false;
    this.isExecuting = false;
    this.createdCount = 0;
    this.cancelRequested = false;

    this.targetStepId = this.getDefaultTargetStepId();

    const preselectedValid = preselectedModuleId
      ? this.moduleOptions.find((opt) => opt.id === preselectedModuleId && !opt.disabled)?.id
      : null;
    this.selectedModuleId = preselectedValid ?? null;
    this.currentWizardStep = this.hasSeenIntro
      ? this.selectedModuleId
        ? 'step'
        : 'module'
      : 'intro';

    this.position = this.canUseAfterSource ? 'after-source' : 'top';
    this.isDialogOpen = true;
    trackAction('breakout_started', entryLabel ?? 'modal', uniqueIds.length > 1);
  }

  closeDialog() {
    this.isDialogOpen = false;
    this.sourceCardIds = [];
    this.triggeredModuleId = null;
    this.selectedModuleId = null;
    this.currentWizardStep = 'module';
    this.keepOriginals = true;
    this.targetStepId = null;
    this.position = 'after-source';
    this.showPreview = false;
    this.isExecuting = false;
    this.createdCount = 0;
    this.cancelRequested = false;
  }

  dismissIntro() {
    this.hasSeenIntro = true;
    this.currentWizardStep = this.triggeredModuleId ? 'step' : 'module';
    trackAction('breakout_wizard_step_changed', 'intro_done');
  }

  setSelectedModuleId(moduleId: string | null) {
    this.selectedModuleId = moduleId;
  }

  setKeepOriginals(value: boolean) {
    this.keepOriginals = value;
    if (!this.canUseAfterSource && this.position === 'after-source') {
      this.position = 'top';
    }
  }

  setTargetStepId(stepId: string | null) {
    this.targetStepId = stepId;
    if (!this.canUseAfterSource && this.position === 'after-source') {
      this.position = 'top';
    }
  }

  setPosition(position: BreakoutPosition) {
    this.position = position;
  }

  private get wizardSteps(): BreakoutWizardStep[] {
    return this.isSingleCardFixedModuleMode
      ? ['step', 'position', 'original', 'review']
      : ['module', 'step', 'position', 'original', 'review'];
  }

  goToWizardStep(step: BreakoutWizardStep) {
    if (step === 'intro') {
      this.currentWizardStep = 'intro';
      trackAction('breakout_wizard_step_changed', step);
      return;
    }
    if (!this.wizardSteps.includes(step)) return;
    this.currentWizardStep = step;
    trackAction('breakout_wizard_step_changed', step);
  }

  goToNextWizardStep() {
    if (!this.canMoveToNextWizardStep) return;
    const currentIndex = this.wizardSteps.indexOf(this.currentWizardStep);
    if (currentIndex === -1 || currentIndex >= this.wizardSteps.length - 1) return;
    this.currentWizardStep = this.wizardSteps[currentIndex + 1];
    trackAction('breakout_wizard_step_changed', this.currentWizardStep);
  }

  goToPreviousWizardStep() {
    const currentIndex = this.wizardSteps.indexOf(this.currentWizardStep);
    if (currentIndex <= 0) return;
    this.currentWizardStep = this.wizardSteps[currentIndex - 1];
    trackAction('breakout_wizard_step_changed', this.currentWizardStep);
  }

  togglePreview() {
    this.showPreview = !this.showPreview;
    trackAction('breakout_preview_toggled', this.showPreview ? 'show' : 'hide');
  }

  async execute() {
    if (!this.selectedModuleId) {
      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: 'Please select a module.',
      });
      return;
    }
    if (this.sourceCards.length === 0) {
      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: 'No cards selected for breakout.',
      });
      return;
    }
    if (this.totalNewCards === 0) {
      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: 'No cards to create.',
      });
      return;
    }

    runInAction(() => {
      this.isExecuting = true;
      this.createdCount = 0;
      this.cancelRequested = false;
    });

    try {
      await this.rootStore.sessionCardActions.breakoutCards({
        sourceCardIds: this.sourceCards.map((card) => card.id),
        moduleId: this.selectedModuleId,
        keepOriginals: this.keepOriginals,
        targetStepId: this.targetStepId,
        position: this.position,
        onProgress: (created) => {
          runInAction(() => {
            this.createdCount = created;
          });
        },
        isCancelled: () => this.cancelRequested,
      });

      trackAction('breakout_executed', undefined, this.totalNewCards > 1);
      runInAction(() => {
        this.closeDialog();
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : '';
      if (message === 'BREAKOUT_CANCELLED') {
        trackAction('breakout_cancelled');
        runInAction(() => {
          this.isExecuting = false;
          this.closeDialog();
        });
        this.rootStore.uiSnackbarStore.setSnackbarDetails({ description: 'Breakout cancelled' });
        return;
      }
      runInAction(() => {
        this.isExecuting = false;
      });
      this.rootStore.uiSnackbarStore.setSnackbarDetails({
        description: 'Unable to break out cards. Please try again.',
      });
    }
  }

  cancel() {
    if (this.isExecuting) {
      this.cancelRequested = true;
      return;
    }
    this.closeDialog();
  }
}
